import React, { useState } from "react";
import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import Button from "@mui/material/Button";
import Chip from "@mui/material/Chip";
import Paper from "@mui/material/Paper";
import Stack from "@mui/material/Stack";
import Alert from "@mui/material/Alert";
import CircularProgress from "@mui/material/CircularProgress";
import ArrowBack from "@mui/icons-material/ArrowBack";
import CheckIcon from "@mui/icons-material/Check";
import ReportOutlined from "@mui/icons-material/ReportOutlined";
import DescriptionOutlined from "@mui/icons-material/DescriptionOutlined";
import LocationOnOutlined from "@mui/icons-material/LocationOnOutlined";
import FlagOutlined from "@mui/icons-material/FlagOutlined";
import TaskAltOutlined from "@mui/icons-material/TaskAltOutlined";
import AutoAwesomeOutlined from "@mui/icons-material/AutoAwesomeOutlined";
import RefreshOutlined from "@mui/icons-material/RefreshOutlined";
import InfoOutlined from "@mui/icons-material/InfoOutlined";
import ManageSearchOutlined from "@mui/icons-material/ManageSearchOutlined";
import Tooltip from "@mui/material/Tooltip";
import IconButton from "@mui/material/IconButton";
import { useNavigate } from "react-router-dom";
import { CallDetails } from "../../types/Call";
import { CallService } from "../../services/callService";
import { SvgIconComponent } from "@mui/icons-material";

// Map summary line labels to outline icons for professional presentation
const SUMMARY_LINE_ICONS: Record<string, SvgIconComponent> = {
  "Emergency Type": ReportOutlined,
  "Summary": DescriptionOutlined,
  "Location": LocationOnOutlined,
  "Priority": FlagOutlined,
  "Outcome": TaskAltOutlined,
};

/** Parse "Label: Value" lines from the AI summary text, supporting both legacy emoji and clean formats */
function parseSummaryLines(summary: string): { label: string; value: string; Icon: SvgIconComponent }[] {
  return summary
    .split("\n")
    .map((line) => line.trim())
    .filter(Boolean)
    .map((line) => {
      // Strip any leading emoji characters (legacy data) before matching
      const cleaned = line.replace(/^[\p{Emoji_Presentation}\p{Extended_Pictographic}\uFE0F]+\s*/u, "");
      const match = cleaned.match(/^(.+?):\s*(.+)$/);
      if (!match) return null;
      const [, label, value] = match;
      const Icon = SUMMARY_LINE_ICONS[label.trim()] ?? InfoOutlined;
      return { label: label.trim(), value: value.trim(), Icon };
    })
    .filter((item): item is NonNullable<typeof item> => item !== null);
}

/** Detect if a summary looks like raw JSON that wasn't properly formatted */
function isMalformedSummary(summary: string): boolean {
  const trimmed = summary.trim();
  // Starts with { or "Summary (Raw):" prefix — the backend fallback
  if (trimmed.startsWith("Summary (Raw):")) return true;
  // Contains JSON-like patterns: quoted keys with colons
  if (trimmed.startsWith("{") && trimmed.includes('"caseType"')) return true;
  // Has JSON property syntax scattered through lines
  const jsonLineCount = trimmed.split("\n").filter(l => l.trim().match(/^"[^"]+"\s*:/)).length;
  if (jsonLineCount >= 3) return true;
  return false;
}

interface CallDetailsHeaderProps {
  callDetails: CallDetails;
  onBack: () => void;
  onCallDetailsUpdate?: (updatedCallDetails: CallDetails) => void;
  userRole?: string;
}

export const CallDetailsHeader: React.FC<CallDetailsHeaderProps> = ({
  callDetails,
  onBack,
  onCallDetailsUpdate,
  userRole,
}) => {
  const navigate = useNavigate();
  const canAudit = userRole === 'Supervisor' || userRole === 'TenantAdmin';
  const canFindSimilar = canAudit && callDetails.status === 'completed';
  const [updatingStatus, setUpdatingStatus] = useState(false);
  const [auditError, setAuditError] = useState<string | null>(null);
  const [generatingSummary, setGeneratingSummary] = useState(false);

  const handleFindSimilar = () => {
    navigate('/', {
      state: {
        similarToCallId: callDetails.callId,
        similarToIncident: callDetails.incidentNumber,
      }
    });
  };

  const getReviewStatusColor = (
    status?: string
  ): "success" | "warning" | "info" | "error" | "default" => {
    if (!status) return "default";
    switch (status.toLowerCase()) {
      case "audited":
        return "success";
      case "for review":
        return "warning";
      case "auto-audited":
        return "info";
      default:
        return "default";
    }
  };

  const handleMarkAsAudited = async () => {
    try {
      setUpdatingStatus(true);
      setAuditError(null);

      const updatedCallDetails = await CallService.updateReviewStatus(
        callDetails.callId,
        "Audited"
      );

      if (onCallDetailsUpdate) {
        onCallDetailsUpdate(updatedCallDetails);
      }
    } catch (error) {
      console.error("Failed to update review status:", error);
      setAuditError("Failed to mark as audited. Please try again.");
    } finally {
      setUpdatingStatus(false);
    }
  };

  return (
    <Paper sx={{ p: 3 }}>
      <Box
        display="flex"
        justifyContent="space-between"
        alignItems="center"
        mb={3}
      >
        <Button startIcon={<ArrowBack />} onClick={onBack} variant="outlined">
          Back to Home
        </Button>

        <Stack direction="row" spacing={1} alignItems="center">
          {/* Find Similar Calls — Supervisor/TenantAdmin, completed calls only */}
          {canFindSimilar && (
            <Tooltip title="Find calls with similar patterns">
              <IconButton
                onClick={handleFindSimilar}
                size="small"
                aria-label="Find similar calls"
                sx={{ color: 'text.secondary' }}
              >
                <ManageSearchOutlined />
              </IconButton>
            </Tooltip>
          )}

          {/* Review Status */}
          <Box display="flex" alignItems="center" gap={1}>
            <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.75rem' }}>
              Review:
            </Typography>
            <Chip
              label={callDetails.reviewStatus || "Not Set"}
              color={getReviewStatusColor(callDetails.reviewStatus)}
              size="small"
            />

            {/* Mark as Audited button - only show for "For Review" status and privileged roles */}
            {canAudit && callDetails.reviewStatus === "For Review" && (
              <Chip
                label={updatingStatus ? "Marking..." : "Mark as Audited"}
                icon={
                  updatingStatus ? (
                    <CircularProgress size={16} />
                  ) : (
                    <CheckIcon />
                  )
                }
                onClick={handleMarkAsAudited}
                disabled={updatingStatus}
                color="success"
                variant="filled"
                clickable
                sx={{
                  fontWeight: "medium",
                  textTransform: "none",
                  "&:hover": {
                    bgcolor: "success.dark",
                  },
                  "&:disabled": {
                    bgcolor: "grey.300",
                    color: "grey.500",
                  },
                }}
              />
            )}

            {/* Show audit info for audited status */}
            {callDetails.reviewStatus === "Audited" &&
              callDetails.lastReviewedBy && (
                <Typography
                  variant="caption"
                  color="text.secondary"
                  sx={{ fontSize: "0.7rem" }}
                >
                  by {callDetails.lastReviewedBy}
                  {callDetails.lastReviewDate && (
                    <>
                      {" "}
                      •{" "}
                      {new Date(
                        callDetails.lastReviewDate
                      ).toLocaleDateString()}
                    </>
                  )}
                </Typography>
              )}
          </Box>
        </Stack>
      </Box>

      {auditError && (
        <Alert severity="error" onClose={() => setAuditError(null)} sx={{ mb: 2 }}>
          {auditError}
        </Alert>
      )}

      {/* AI Summary - Structured display with outline icons, or generate button */}
      {callDetails.summary &&
       !callDetails.summary.includes("AI configuration incomplete") &&
       !callDetails.summary.includes("AI analysis unavailable") ? (
        <Box
          sx={{
            mb: 3,
            p: 2,
            backgroundColor: isMalformedSummary(callDetails.summary) ? "warning.50" : "action.hover",
            borderRadius: 2,
            border: "1px solid",
            borderColor: isMalformedSummary(callDetails.summary) ? "warning.main" : "primary.light",
          }}
        >
          <Box display="flex" justifyContent="space-between" alignItems="center" mb={1}>
            <Typography
              variant="subtitle2"
              color={isMalformedSummary(callDetails.summary) ? "warning.dark" : "primary.main"}
              fontWeight="bold"
              sx={{ display: "flex", alignItems: "center", gap: 1 }}
            >
              <AutoAwesomeOutlined fontSize="small" />
              AI Summary
              {isMalformedSummary(callDetails.summary) && (
                <Chip label="Formatting Issue" size="small" color="warning" variant="outlined" sx={{ ml: 1 }} />
              )}
            </Typography>
            {canAudit && callDetails.transcriptionText && (
              <Tooltip title="Regenerate AI Summary">
                <IconButton
                  size="small"
                  disabled={generatingSummary}
                  onClick={async () => {
                    try {
                      setGeneratingSummary(true);
                      const updated = await CallService.regenerateSummary(callDetails.callId);
                      if (onCallDetailsUpdate) onCallDetailsUpdate(updated);
                    } catch {
                      // Error handled by notificationService
                    } finally {
                      setGeneratingSummary(false);
                    }
                  }}
                  sx={{ color: "primary.main" }}
                >
                  {generatingSummary ? <CircularProgress size={18} /> : <RefreshOutlined fontSize="small" />}
                </IconButton>
              </Tooltip>
            )}
          </Box>
          {isMalformedSummary(callDetails.summary) ? (
            <Typography variant="body2" color="text.secondary">
              The AI returned a malformed summary. Click the refresh button to regenerate.
            </Typography>
          ) : (
            <Stack spacing={0.75}>
              {parseSummaryLines(callDetails.summary).map(({ label, value, Icon }) => (
                <Box key={label} sx={{ display: "flex", alignItems: "flex-start", gap: 1 }}>
                  <Icon
                    sx={{ fontSize: 18, color: "primary.main", mt: "2px", flexShrink: 0 }}
                  />
                  <Typography variant="body2" sx={{ lineHeight: 1.5, fontSize: "0.9rem" }}>
                    <Box component="span" sx={{ fontWeight: 600, color: "text.secondary" }}>
                      {label}:
                    </Box>{" "}
                    {value}
                  </Typography>
                </Box>
              ))}
            </Stack>
          )}
        </Box>
      ) : callDetails.transcriptionText ? (
        <Box
          sx={{
            mb: 3,
            p: 2,
            backgroundColor: "grey.50",
            borderRadius: 2,
            border: "1px dashed",
            borderColor: "grey.300",
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
          }}
        >
          <Typography variant="body2" color="text.secondary">
            No AI summary available for this call.
          </Typography>
          <Button
            variant="outlined"
            size="small"
            startIcon={generatingSummary ? <CircularProgress size={16} /> : <AutoAwesomeOutlined />}
            disabled={generatingSummary}
            onClick={async () => {
              try {
                setGeneratingSummary(true);
                const updated = await CallService.regenerateSummary(callDetails.callId);
                if (onCallDetailsUpdate) onCallDetailsUpdate(updated);
              } catch {
                // Error handled by notificationService in CallService
              } finally {
                setGeneratingSummary(false);
              }
            }}
          >
            {generatingSummary ? "Generating..." : "Generate AI Summary"}
          </Button>
        </Box>
      ) : null}

      {/* Compact Call Details Grid */}
      <Box
        display="grid"
        gridTemplateColumns={{
          xs: "1fr 1fr",
          sm: "repeat(3, 1fr)",
          md: "repeat(6, 1fr)",
        }}
        gap={2}
      >
        <Box>
          <Typography variant="caption" color="text.secondary" sx={{ fontSize: "0.7rem" }}>
            Incident #
          </Typography>
          <Typography variant="body2" fontWeight="medium" sx={{ fontSize: "0.85rem" }}>
            {callDetails.incidentNumber}
          </Typography>
        </Box>

        <Box>
          <Typography variant="caption" color="text.secondary" sx={{ fontSize: "0.7rem" }}>
            Type
          </Typography>
          <Typography variant="body2" fontWeight="medium" sx={{ fontSize: "0.85rem" }}>
            {callDetails.callType}
          </Typography>
        </Box>

        <Box>
          <Typography variant="caption" color="text.secondary" sx={{ fontSize: "0.7rem" }}>
            Operator
          </Typography>
          <Typography
            variant="body2"
            fontWeight="medium"
            sx={{ fontSize: "0.85rem", overflowWrap: "anywhere" }}
            color={callDetails.operator || callDetails.operatorId ? undefined : "text.secondary"}
          >
            {callDetails.operator || callDetails.operatorId || "Not set"}
          </Typography>
        </Box>

        <Box>
          <Typography variant="caption" color="text.secondary" sx={{ fontSize: "0.7rem" }}>
            Duration
          </Typography>
          <Typography variant="body2" fontWeight="medium" sx={{ fontSize: "0.85rem" }}>
            {callDetails.fileDurationFormatted || "Unknown"}
          </Typography>
        </Box>

        <Box>
          <Typography variant="caption" color="text.secondary" sx={{ fontSize: "0.7rem" }}>
            Incident Date
          </Typography>
          <Typography variant="body2" fontWeight="medium" sx={{ fontSize: "0.85rem" }}>
            {new Date(callDetails.callDate).toLocaleDateString("en-US", {
              month: "2-digit",
              day: "2-digit",
              year: "numeric",
            })}
          </Typography>
        </Box>

        <Box>
          <Typography variant="caption" color="text.secondary" sx={{ fontSize: "0.7rem" }}>
            Case Type
          </Typography>
          <Typography variant="body2" fontWeight="medium" sx={{ fontSize: "0.85rem" }}>
            {callDetails.caseType || "N/A"}
          </Typography>
        </Box>
      </Box>
    </Paper>
  );
};
